import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Paperclip,
  UploadSimple,
  File,
  FilePdf,
  FileImage,
  FileDoc,
  FileXls,
  Download,
  Trash,
  Eye,
  X,
  Plus,
  CircleNotch,
  CheckCircle,
  Warning,
  PencilSimple,
  Check as CheckIcon
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// File type icons
const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return FilePdf;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return FileImage;
  if (['doc', 'docx'].includes(ext)) return FileDoc;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileXls;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// Check if file is previewable
const isPreviewable = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
};

export default function AttachmentManager({
  open,
  onOpenChange,
  documentId,
  documentNumber,
  documentType, // 'invoice', 'quotation', 'po', 'grn', etc.
  onAttachmentsChange
}) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // Files queued for upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFilename, setPreviewFilename] = useState('');

  // Fetch attachments when modal opens
  React.useEffect(() => {
    if (open && documentId) {
      fetchAttachments();
    }
  }, [open, documentId]);

  const fetchAttachments = async () => {
    setLoading(true);
    try {
      const endpoint = documentType === 'quotation' ? 'quotations' : 
                       documentType === 'po' ? 'purchase_orders' : 
                       documentType === 'grn' ? 'grns' : 'invoices';
      const response = await axios.get(`${API_URL}/documents/attachments/${endpoint}/${documentId}`);
      setAttachments(response.data.attachments || []);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
      toast.error('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  // Add files to upload queue
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 20MB)`);
        return false;
      }
      return true;
    });

    // Add to queue with unique IDs
    const newQueueItems = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      status: 'pending', // pending, uploading, success, error
      progress: 0
    }));

    setUploadQueue(prev => [...prev, ...newQueueItems]);
    e.target.value = ''; // Reset input
  };

  // Remove file from queue
  const removeFromQueue = (queueId) => {
    setUploadQueue(prev => prev.filter(item => item.id !== queueId));
  };

  // Upload all queued files
  const uploadQueuedFiles = async () => {
    if (uploadQueue.length === 0) return;

    setUploading(true);
    const endpoint = documentType === 'quotation' ? 'quotations' : 
                     documentType === 'po' ? 'purchase_orders' : 
                     documentType === 'grn' ? 'grns' : 'invoices';

    for (const item of uploadQueue) {
      if (item.status === 'success') continue;

      setUploadQueue(prev => prev.map(q => 
        q.id === item.id ? { ...q, status: 'uploading' } : q
      ));

      try {
        const formData = new FormData();
        formData.append('file', item.file);

        await axios.post(
          `${API_URL}/documents/attachments/${endpoint}/${documentId}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadQueue(prev => prev.map(q => 
                q.id === item.id ? { ...q, progress } : q
              ));
            }
          }
        );

        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, status: 'success', progress: 100 } : q
        ));

      } catch (error) {
        console.error('Upload failed:', error);
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, status: 'error' } : q
        ));
      }
    }

    setUploading(false);
    fetchAttachments(); // Refresh attachment list
    
    // Clear successful uploads from queue after a delay
    setTimeout(() => {
      setUploadQueue(prev => prev.filter(q => q.status !== 'success'));
    }, 2000);

    if (onAttachmentsChange) {
      onAttachmentsChange();
    }
  };

  // Download attachment
  const handleDownload = async (attachmentId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/documents/attachments/download/${attachmentId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${filename}`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
    }
  };

  // Preview attachment
  const handlePreview = async (attachmentId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/documents/attachments/download/${attachmentId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewFilename(filename);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error('Failed to preview file');
    }
  };

  // Delete attachment
  const handleDelete = async (attachmentId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API_URL}/documents/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchAttachments();
      if (onAttachmentsChange) {
        onAttachmentsChange();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete attachment');
    }
  };

  // ===== Inline rename (custom_name) =====
  // Backend endpoint: PATCH /api/documents/attachments/{id} { custom_name }
  // Display precedence: custom_name → original_filename.
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  const displayName = (att) => (att?.custom_name || att?.original_filename || '');

  const startRename = (att) => {
    setRenamingId(att.id);
    setRenameDraft(displayName(att));
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft('');
  };

  const commitRename = async (att) => {
    const name = (renameDraft || '').trim();
    if (!name || name === displayName(att)) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      await axios.patch(`${API_URL}/documents/attachments/${att.id}`, { custom_name: name });
      toast.success('Renamed');
      cancelRename();
      fetchAttachments();
      if (onAttachmentsChange) onAttachmentsChange();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = 'Failed to rename';
      if (typeof raw === 'string') msg = raw;
      else if (raw && typeof raw === 'object') msg = raw.message || raw.code || JSON.stringify(raw);
      else if (err?.message) msg = err.message;
      toast.error(msg);
    } finally {
      setRenameSaving(false);
    }
  };

  // Close preview and cleanup URL
  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewOpen(false);
    setPreviewUrl('');
    setPreviewFilename('');
  };

  const isPdf = previewFilename?.toLowerCase().endsWith('.pdf');

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip size={20} />
              Attachments - {documentNumber}
            </DialogTitle>
            <DialogDescription>
              Upload, view, and manage document attachments. You can add multiple files.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Upload Area */}
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 hover:border-primary/50 transition-colors">
              <input
                type="file"
                id="attachment-upload-multi"
                className="hidden"
                onChange={handleFileSelect}
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp,.txt"
                data-testid="attachment-file-input"
              />
              <label htmlFor="attachment-upload-multi" className="cursor-pointer block text-center">
                <UploadSimple size={28} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">Click to add files</p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, DOC, XLS, Images (max 20MB each)
                </p>
              </label>
            </div>

            {/* Upload Queue */}
            {uploadQueue.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Files to Upload ({uploadQueue.length})</h4>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setUploadQueue([])}
                      disabled={uploading}
                    >
                      Clear All
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={uploadQueuedFiles}
                      disabled={uploading || uploadQueue.every(q => q.status === 'success')}
                      data-testid="upload-all-btn"
                    >
                      {uploading ? (
                        <>
                          <CircleNotch size={16} className="animate-spin mr-1" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <UploadSimple size={16} className="mr-1" />
                          Upload All
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {uploadQueue.map((item) => {
                    const FileIcon = getFileIcon(item.file.name);
                    return (
                      <div 
                        key={item.id} 
                        className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg"
                      >
                        <FileIcon size={20} className="text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{item.file.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">
                              {formatFileSize(item.file.size)}
                            </span>
                            {item.status === 'uploading' && (
                              <Progress value={item.progress} className="h-1 flex-1" />
                            )}
                            {item.status === 'success' && (
                              <Badge variant="outline" className="text-green-600 border-green-600">
                                <CheckCircle size={12} className="mr-1" /> Uploaded
                              </Badge>
                            )}
                            {item.status === 'error' && (
                              <Badge variant="outline" className="text-red-600 border-red-600">
                                <Warning size={12} className="mr-1" /> Failed
                              </Badge>
                            )}
                          </div>
                        </div>
                        {item.status === 'pending' && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => removeFromQueue(item.id)}
                            className="flex-shrink-0"
                          >
                            <X size={16} />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Divider */}
            {(uploadQueue.length > 0 && attachments.length > 0) && (
              <div className="border-t border-border" />
            )}

            {/* Existing Attachments */}
            <div>
              <h4 className="text-sm font-medium mb-2">
                Uploaded Files ({attachments.length})
              </h4>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch size={24} className="animate-spin text-primary" />
                </div>
              ) : attachments.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-6">
                  No attachments yet. Add files above.
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {attachments.map((att) => {
                    const shownName = displayName(att);
                    const FileIcon = getFileIcon(shownName);
                    const canPreview = isPreviewable(shownName);
                    const hasThumbnail = att.thumbnail && !att.thumbnail.includes('svg+xml');
                    const isRenaming = renamingId === att.id;
                    
                    return (
                      <div 
                        key={att.id} 
                        className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                        data-testid={`attachment-item-${att.id}`}
                      >
                        {/* Thumbnail or Icon */}
                        {hasThumbnail ? (
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0 border">
                            <img 
                              src={att.thumbnail} 
                              alt={shownName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <FileIcon size={24} className="text-primary" weight="duotone" />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          {isRenaming ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                value={renameDraft}
                                onChange={(e) => setRenameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); commitRename(att); }
                                  else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                                }}
                                autoFocus
                                disabled={renameSaving}
                                className="h-7 text-sm"
                                data-testid={`rename-input-${att.id}`}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => commitRename(att)}
                                disabled={renameSaving}
                                className="h-7 px-2 text-emerald-600 hover:text-emerald-700"
                                title="Save"
                                data-testid={`rename-save-${att.id}`}
                              >
                                {renameSaving ? <CircleNotch size={14} className="animate-spin" /> : <CheckIcon size={14} />}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelRename}
                                disabled={renameSaving}
                                className="h-7 px-2"
                                title="Cancel"
                                data-testid={`rename-cancel-${att.id}`}
                              >
                                <X size={14} />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 group">
                              <p
                                className="text-sm font-medium truncate cursor-pointer hover:underline"
                                onClick={() => startRename(att)}
                                title="Click to rename"
                                data-testid={`att-name-${att.id}`}
                              >
                                {shownName}
                              </p>
                              <button
                                onClick={() => startRename(att)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary shrink-0"
                                title="Rename"
                                data-testid={`rename-att-${att.id}`}
                              >
                                <PencilSimple size={12} />
                              </button>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(att.file_size)} • {new Date(att.uploaded_at).toLocaleDateString()}
                            {att.custom_name && att.original_filename && att.custom_name !== att.original_filename && (
                              <span className="ml-1 italic opacity-70">(orig: {att.original_filename})</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {canPreview && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handlePreview(att.id, shownName)}
                              title="Preview"
                              data-testid={`preview-att-${att.id}`}
                            >
                              <Eye size={16} />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDownload(att.id, shownName)}
                            title="Download"
                            data-testid={`download-att-${att.id}`}
                          >
                            <Download size={16} />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDelete(att.id, shownName)}
                            className="text-red-500 hover:text-red-600"
                            title="Delete"
                            data-testid={`delete-att-${att.id}`}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal - Using higher z-index to ensure it appears above main dialog */}
      {previewOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80" onClick={closePreview}>
          <div 
            className="bg-background rounded-lg max-w-4xl max-h-[90vh] w-[90vw] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <Eye size={20} />
                {previewFilename}
              </h3>
              <Button variant="ghost" size="sm" onClick={closePreview}>
                <X size={20} />
              </Button>
            </div>
            
            <div className="flex-1 overflow-auto bg-muted/30 min-h-[400px] p-2">
              {isPdf ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full min-h-[500px] rounded-lg"
                  title={previewFilename}
                />
              ) : (
                <div className="flex items-center justify-center p-4">
                  <img
                    src={previewUrl}
                    alt={previewFilename}
                    className="max-w-full max-h-[600px] object-contain rounded-lg"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end p-4 border-t">
              <Button variant="outline" onClick={closePreview}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
